import React, { forwardRef, useRef, useState } from "react";
import Table from "react-bootstrap/Table";
import moment from "moment";
import Swal from "../../messageBox/customSweetAlert";
import axios from "axios";
import { DELETE_TRIP_V1, EDIT_TRIP_MILES } from "../../constant";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/loaderAction";
import MapComponent from "../MapComponent";
import { Dialog } from "primereact/dialog";
import TripPDF from "../pdfContent/TripPDF";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

const TripTableV1 = forwardRef(
  ({ tableData, setTableData, onDeleteSuccess }, ref) => {
    const dispatch = useDispatch();
    const pdfRef = useRef();
    const [savingMilesFor, setSavingMilesFor] = useState(null);
    const [visible, setVisible] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [loadingRow, setLoadingRow] = useState(null);
    const [pdfDetails, setPdfDetails] = useState(null);

    const getPassengerData = (row) =>
      row?.passenger || row?.schedule?.passenger || {};
    const getScheduleTranData = (row) => row?.schedule_tran || row || {};

    const getPassengerName = (row) =>
      `${getPassengerData(row)?.first_name || ""} ${getPassengerData(row)?.middle_name || ""} ${getPassengerData(row)?.last_name || ""}`
        .replace(/\s+/g, " ")
        .trim();

    const getDriverName = (row) => {
      const d = row?.driver;
      if (!d) return "";
      const full = d.full_name;
      if (full != null && String(full).trim() !== "") {
        return String(full).trim();
      }
      return `${d.first_name || ""} ${d.middle_name || ""} ${d.last_name || ""}`
        .replace(/\s+/g, " ")
        .trim();
    };

    const getCompId = (row) => {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      return (
        row?.comp_id ||
        row?.company_id ||
        userData?.company_id ||
        userData?.comp_id
      );
    };

    const handleMapIconClick = (row) => {
      setSelectedTrip({
        trip: {
          pickup_latitude: row?.pickup_latitude,
          pickup_longitude: row?.pickup_longitude,
          dropoff_latitude: row?.dropoff_latitude,
          dropoff_longitude: row?.dropoff_longitude,
          pick_up_time: row?.trip_pick_up_time,
          drop_off_time: row?.trip_drop_off_time,
        },
      });
      setVisible(true);
    };

    const buildTripSheetPayload = (row) => {
      const schTran = getScheduleTranData(row);
      return {
        details: {
          schedule_id: Number(row?.schedule_id) || row?.schedule?.schedule_id,
          schedule_date: row?.schedule?.schedule_date || row?.trip_date,
          trip_date: row?.trip_date || row?.schedule?.schedule_date,
          dead_head_miles: row?.schedule?.dead_head_miles,
          scheduled_miles: row?.miles ?? schTran?.miles,
          vehicle: row?.vehicle || null,
          comp_id: getCompId(row),
          schedule_note: row?.schedule_note || "",
          passengerDetails: {
            passengerId: row?.passenger?.user_id,
            passengerFirstName: row?.passenger?.first_name || "",
            passengerMiddleName: row?.passenger?.middle_name || "",
            passengerLastName: row?.passenger?.last_name || "",
            passenger: row?.passenger?.passenger || {},
          },
          driverDetails: {
            driverId: row?.driver?.user_id,
            fullName: row?.driver?.full_name || "",
            firstName: row?.driver?.first_name || "",
            middleName: row?.driver?.middle_name || "",
            lastName: row?.driver?.last_name || "",
          },
        },
        myArray: {
          [Number(row?.schedule_id) || row?.schedule?.schedule_id]: [
            {
              schedule_trans_id: row?.schedule_trans_id,
              pick_up_time: schTran?.pick_up_time,
              signature_file: schTran?.signature_file,
              driver_signature_file: schTran?.driver_signature_file,
              trip: {
                pickup_location: row?.trip_pick_up_location,
                dropOffLocation: row?.trip_drop_off_location,
                pick_up_time: row?.trip_pick_up_time,
                drop_off_time: row?.trip_drop_off_time,
                startOdometer: row?.odo_meter_pickup,
                endOdometer: row?.odo_meter_dropoff,
                miles: schTran?.miles,
                vehicle: row?.schedule?.vehicle || null,
              },
            },
          ],
        },
      };
    };

    const handleTripSheetClick = (row, index) => {
      const { details, myArray } = buildTripSheetPayload(row);
      localStorage.setItem("pdfDetails", JSON.stringify(details));
      localStorage.setItem("myArray", JSON.stringify(myArray));
      setLoadingRow(index);
      setPdfDetails(details);
      setTimeout(() => {
        const input = pdfRef.current;
        if (!input) {
          setLoadingRow(null);
          return;
        }
        html2canvas(input, { useCORS: true, allowTaint: true }).then(
          (canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const imgWidth = 210;
            const pageHeight = 297;
            const padding = 10;
            const contentWidth = imgWidth - padding * 2;
            const imgHeight = (canvas.height * contentWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(
              imgData,
              "PNG",
              padding,
              position,
              contentWidth,
              imgHeight,
            );
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(
                imgData,
                "PNG",
                padding,
                position,
                contentWidth,
                imgHeight,
              );
              heightLeft -= pageHeight;
            }

            pdf.save(`Trip_Details_${details.schedule_id}.pdf`);
            setLoadingRow(null);
          },
        );
      }, 1000);
    };

    const handleDelete = (row) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This will permanently remove the selected trip leg.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        dispatch(setLoading(true));
        try {
          const response = await axios.delete(
            `${DELETE_TRIP_V1}/${row?.schedule_trans_id}`,
          );
          if (response?.data?.status === 200) {
            setTableData((prev) =>
              prev.filter(
                (item) =>
                  String(item?.schedule_trans_id) !==
                  String(row?.schedule_trans_id),
              ),
            );
            onDeleteSuccess?.(row?.schedule_trans_id);
          } else {
            Swal.fire(
              "Error",
              response?.data?.message || "Delete failed",
              "error",
            );
          }
        } catch (error) {
          Swal.fire(
            "Error",
            error?.response?.data?.message || error?.message || "Delete failed",
            "error",
          );
        } finally {
          dispatch(setLoading(false));
        }
      });
    };

    const handleEditMiles = async (row) => {
      const initialMiles =
        getScheduleTranData(row)?.miles != null
          ? String(getScheduleTranData(row).miles)
          : "";
      const { value: miles } = await Swal.fire({
        title: "Edit Scheduled Miles",
        input: "text",
        inputValue: initialMiles,
        inputPlaceholder: "Enter miles",
        showCancelButton: true,
        confirmButtonText: "Save",
        inputValidator: (value) => {
          const v = String(value || "").trim();
          if (!v) return "Miles is required";
          if (!/^\d+(\.\d+)?$/.test(v)) return "Enter a valid number";
          return null;
        },
      });

      if (miles == null) return;
      const passengerId = getPassengerData(row)?.user_id;
      if (!passengerId) {
        Swal.fire("Error", "Passenger is missing for this trip.", "error");
        return;
      }

      setSavingMilesFor(row?.schedule_trans_id);
      dispatch(setLoading(true));
      try {
        const response = await axios.patch(
          `${EDIT_TRIP_MILES}/${row?.schedule_trans_id}`,
          {
            miles: String(miles).trim(),
            passengerId,
          },
        );
        if (
          response?.data?.status === 200 ||
          response?.data?.status === "200"
        ) {
          setTableData((prev) =>
            prev.map((item) =>
              String(item?.schedule_trans_id) === String(row?.schedule_trans_id)
                ? {
                    ...item,
                    schedule_tran: {
                      ...(item?.schedule_tran || {}),
                      miles: String(miles).trim(),
                    },
                    miles: String(miles).trim(),
                  }
                : item,
            ),
          );
          Swal.fire(
            "Success!",
            response?.data?.message || "Miles updated",
            "success",
          );
        } else {
          Swal.fire(
            "Error",
            response?.data?.message || "Update failed",
            "error",
          );
        }
      } catch (error) {
        Swal.fire(
          "Error",
          error?.response?.data?.message || error?.message || "Update failed",
          "error",
        );
      } finally {
        setSavingMilesFor(null);
        dispatch(setLoading(false));
      }
    };

    return (
      <>
        <div style={{ overflowX: "auto", width: "100%" }}>
          <Table
            id="trip-table"
            bordered
            className="tbl-require table-schedule tripTable"
            style={{
              border: "2px solid rgba(224, 224, 224, 1)",
              minWidth: "100%",
            }}
          >
            <thead>
              <tr>
                <th>Trip Date</th>
                <th>Passenger</th>
                <th>Driver</th>
                <th>Payor</th>
                <th>Pickup / Drop Off Details</th>
                {/* <th>Drop Off Details</th> */}
                <th title="Scheduled Miles">Sch. Miles</th>
                <th>Dead Head Miles</th>
                <th>Map</th>
                <th>Passenger Sign</th>
                <th>Driver Sign</th>
                <th>Trip Sheet</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(tableData) && tableData.length > 0 ? (
                tableData.map((row, index) => (
                  <tr key={row?.schedule_trans_id}>
                    <td>
                      {moment(
                        row?.trip_date ||
                          row?.trip?.trip_date ||
                          row?.schedule?.schedule_date,
                      ).format("MM/DD/YYYY")}
                    </td>
                    <td>{getPassengerName(row)}</td>
                    <td>{getDriverName(row) || "N/A"}</td>
                    <td>
                      {getPassengerData(row)?.passenger?.payerDetails
                        ?.payer_name || "N/A"}
                    </td>
                    <td>
                      {row?.schedule_tran?.trip_status === 3 ? (
                        <>
                          <div>
                            <span
                              style={{ color: "#105c64", fontWeight: "bold" }}
                            >
                              Leg:
                            </span>{" "}
                            {row?.schedule_tran?.legNO || "N/A"}
                          </div>
                          <span style={{ color: "red" }}>No Show</span>
                        </>
                      ) : (
                        <div>
                          <div>
                            <span
                              style={{ color: "#105c64", fontWeight: "bold" }}
                            >
                              Leg:
                            </span>{" "}
                            {row?.schedule_tran?.legNO || "N/A"}
                          </div>
                          <div>
                            <span
                              style={{ color: "#105c64", fontWeight: "bold" }}
                            >
                              Pickup:
                            </span>{" "}
                            {row?.trip_pick_up_location ||
                              row?.trip?.trip_pick_up_location ||
                              "N/A"}
                          </div>
                          <div>
                            <span
                              style={{ color: "#105c64", fontWeight: "bold" }}
                            >
                              Drop Off:
                            </span>{" "}
                            {row?.trip_drop_off_location ? (
                              row?.trip_drop_off_location
                            ) : (
                              <span style={{ color: "red" }}>
                                Trip in progress
                              </span>
                            )}
                          </div>
                          <div>
                            <span
                              style={{ color: "#105c64", fontWeight: "bold" }}
                            >
                              Time:
                            </span>{" "}
                            {row?.trip_pick_up_time
                              ? moment(
                                  row.trip_pick_up_time,
                                  "HH:mm:ss",
                                ).format("hh:mm a")
                              : "N/A"}{" "}
                            <i
                              className="pi pi-arrow-right"
                              style={{ fontSize: "11px" }}
                            ></i>{" "}
                            {row?.trip_drop_off_time
                              ? moment(
                                  row.trip_drop_off_time,
                                  "HH:mm:ss",
                                ).format("hh:mm a")
                              : "N/A"}
                          </div>
                          <span style={{ color: "#105c64", fontWeight: "bold" }}>Start Odometer:</span> {row?.odo_meter_pickup || "N/A"}
                          <span style={{ color: "#105c64", fontWeight: "bold",marginLeft: "10px" }}>End Odometer:</span> {row?.odo_meter_dropoff || "N/A"}
                          <span style={{ color: "#105c64", fontWeight: "bold",marginLeft: "10px" }}>Actual Miles:</span> {Math.round(row?.odo_meter_dropoff - row?.odo_meter_pickup) || 0}
                        </div>
                      )}
                    </td>
                    <td>
                      {getScheduleTranData(row)?.miles ?? row?.miles ?? "N/A"}
                      <i
                        className="pi pi-pen-to-square icon-btn-edit cursor-pointer"
                        title="Edit miles"
                        style={{
                          fontSize: "13px",
                          marginLeft: "6px",
                          opacity:
                            savingMilesFor === row?.schedule_trans_id ? 0.4 : 1,
                          pointerEvents:
                            savingMilesFor === row?.schedule_trans_id
                              ? "none"
                              : "auto",
                        }}
                        onClick={() => handleEditMiles(row)}
                      ></i>
                    </td>
                    <td>
                      {row?.schedule_tran?.legNO == 1 ? row?.schedule?.dead_head_miles ? row?.schedule?.dead_head_miles : "N/A" : "N/A"}
                    </td>

                    <td
                      className="no-export no-pdf"
                      style={{ textAlign: "center" }}
                    >
                      {row?.pickup_latitude &&
                      row?.pickup_longitude &&
                      row?.dropoff_latitude &&
                      row?.dropoff_longitude ? (
                        <i
                          className="pi pi-map-marker cursor-pointer"
                          onClick={() => handleMapIconClick(row)}
                          title="View Map"
                          style={{ color: "green", fontSize: "16px" }}
                        ></i>
                      ) : (
                        <span style={{ color: "red" }}>No Map</span>
                      )}
                    </td>
                    <td className="no-export no-pdf">
                      {getScheduleTranData(row)?.signature_file ? (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/responses/uploads/${getCompId(row)}/signature/passenger/${row?.schedule_trans_id}/${getScheduleTranData(row)?.signature_file}`}
                          alt="Passenger Signature"
                          style={{
                            width: "80px",
                            height: "50px",
                            objectFit: "contain",
                            margin: "5px",
                          }}
                        />
                      ) : (
                        <span style={{ color: "red" }}>No Signature</span>
                      )}
                    </td>
                    <td className="no-export no-pdf">
                      {getScheduleTranData(row)?.driver_signature_file ? (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/responses/uploads/${getCompId(row)}/signature/driver/${row?.schedule_trans_id}/${getScheduleTranData(row)?.driver_signature_file}`}
                          alt="Driver Signature"
                          style={{
                            width: "80px",
                            height: "50px",
                            objectFit: "contain",
                            margin: "5px",
                          }}
                        />
                      ) : (
                        <span style={{ color: "red" }}>No Signature</span>
                      )}
                    </td>
                    <td
                      className="no-export no-pdf"
                      style={{ textAlign: "center" }}
                    >
                      {loadingRow === index ? (
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          style={{ color: "#dd0f0f", fontSize: "17px" }}
                          className="fa-2x"
                        />
                      ) : (
                        <i
                          className="pi pi-file-pdf cursor-pointer"
                          onClick={() => handleTripSheetClick(row, index)}
                          title="Trip Sheet"
                          style={{ color: "red", fontSize: "16px" }}
                        ></i>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <i
                        className="pi pi-trash icon-btn-delete delete-icon cursor-pointer"
                        onClick={() => handleDelete(row)}
                        title="Delete Trip"
                        style={{ color: "red", fontSize: "16px" }}
                      ></i>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="align-middle text-align" colSpan="12">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            top: "-9999px",
            left: "-9999px",
          }}
        >
          <Table
            ref={ref}
            bordered
            className="tbl-require table-schedule tripTable"
          >
            <thead>
              <tr>
                <th>Trip Date</th>
                <th>Passenger</th>
                <th>Driver</th>
                <th>Payer</th>
                <th>Pickup</th>
                <th>Drop Off</th>
                <th>Sch. Miles</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(tableData) &&
                tableData.map((row) => (
                  <tr key={`export-${row?.schedule_trans_id}`}>
                    <td>
                      {moment(
                        row?.trip_date ||
                          row?.trip?.trip_date ||
                          row?.schedule?.schedule_date,
                      ).format("MM/DD/YYYY")}
                    </td>
                    <td>{getPassengerName(row)}</td>
                    <td>{getDriverName(row) || "N/A"}</td>
                    <td>
                      {getPassengerData(row)?.passenger?.payerDetails
                        ?.payer_name || "N/A"}
                    </td>
                    <td>
                      {row?.trip_pick_up_location ||
                        row?.trip?.trip_pick_up_location ||
                        "N/A"}
                    </td>
                    <td>
                      {row?.trip_drop_off_location ||
                        row?.trip?.trip_drop_off_location ||
                        "N/A"}
                    </td>
                    <td>
                      {getScheduleTranData(row)?.miles ?? row?.miles ?? "N/A"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
        {pdfDetails && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              overflow: "hidden",
              top: "-9999px",
              left: "-9999px",
            }}
          >
            <TripPDF
              ref={pdfRef}
              details={pdfDetails}
              myArray={JSON.parse(localStorage.getItem("myArray") || "{}")}
            />
          </div>
        )}
        {selectedTrip && (
          <Dialog
            visible={visible}
            header="Map"
            onHide={() => setVisible(false)}
            style={{ width: "50rem" }}
          >
            <MapComponent location={selectedTrip} />
          </Dialog>
        )}
      </>
    );
  },
);

export default TripTableV1;
